import React from 'react';
import EventIcon from '@mui/icons-material/Event';
import HomeIcon from '@mui/icons-material/Home';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box } from '@mui/material';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';

export const stepTitle = 'Tell us about your place';
export const stepDescription =
  "Let's get the basics. We'll cover the property type, location, and your rental terms.";

const PropertyBasicsIntro: React.FC = () => (
  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        minHeight: 500,
        width: '100%',
        maxWidth: 1100,
        gap: { xs: 4, md: 8 },
        padding: { xs: '16px 0', md: '32px 0' },
        textAlign: { xs: 'center', md: 'left' },
      }}
    >
      {/* Left: Text content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          width: { xs: '100%', md: '50%' },
          order: { xs: 2, md: 1 }, // Text appears below image on mobile
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{ color: 'text.secondary', fontWeight: 500, mb: 1 }}
        >
          Step 1
        </Typography>
        <Typography
          variant='h3'
          sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '3rem' },
            lineHeight: 1.1,
            mb: 2,
          }}
        >
          Tell us about your place
        </Typography>
        <Typography
          variant='body1'
          sx={{ color: 'text.secondary', fontSize: '1.125rem', maxWidth: 500 }}
        >
          {
            "Let's get the basics. We'll cover the property type, location, and your rental terms."
          }
        </Typography>
      </Box>

      {/* Right: Illustration */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: { xs: '80%', sm: '60%', md: '50%' },
          order: { xs: 1, md: 2 }, // Image appears above text on mobile
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: 350,
            height: 280,
            backgroundColor: '#f0f9ff',
            borderRadius: 4,
            border: '2px solid #bfdbfe',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(34, 197, 94, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 50% 50%, rgba(249, 115, 22, 0.1) 0%, transparent 50%)
              `,
            }}
          />

          {/* Main house icon */}
          <HomeIcon
            sx={{
              fontSize: 64,
              color: '#3b82f6',
              mb: 2,
              zIndex: 1,
            }}
          />

          {/* Feature indicators */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              zIndex: 1,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: 1,
                backgroundColor: 'white',
                borderRadius: 4,
                border: '1px solid #d1d5db',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              <LocationOnIcon sx={{ fontSize: 16, color: '#ef4444' }} />
              <Typography
                variant='caption'
                sx={{ fontWeight: 500, color: '#374151' }}
              >
                Location
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: 1,
                backgroundColor: 'white',
                borderRadius: 4,
                border: '1px solid #d1d5db',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              <EventIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
              <Typography
                variant='caption'
                sx={{ fontWeight: 500, color: '#374151' }}
              >
                Terms
              </Typography>
            </Box>
          </Box>

          {/* Decorative elements */}
          <Box
            sx={{
              position: 'absolute',
              top: 30,
              right: 30,
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#22c55e',
              animation: 'pulse 2s infinite',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 40,
              left: 40,
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#8b5cf6',
              animation: 'pulse 2s infinite 0.5s',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 50,
              left: 30,
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: '#f59e0b',
              animation: 'pulse 2s infinite 1s',
            }}
          />

          {/* Property type indicators */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 15,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
            }}
          >
            {[1, 2, 3].map(i => (
              <Box
                key={i}
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: '#94a3b8',
                  animation: `pulse 1.5s infinite ${i * 0.2}s`,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

export default PropertyBasicsIntro;
